import { useState, useCallback, useRef, useEffect } from 'react';
import { executeQueryV2, cancelQueryV2 } from '../../../database-objects/services/queryExecutor';
import { formatExecutionTime } from '../../common/utils/formatExecutionTime';
import { nextCancelRequest } from '../../common/utils/cancelEscalation';
import { formatExecutionRowSummary, summarizeExecutionRows } from '../utils/executionRowSummary';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { createExecutionId } from '../../../../types/queryExecution';
import type { QueryResultSet, QueryExecutionError, QueryExecutionMessage } from '../../../../types/queryExecution';
import { isConnectionLostError, type DatabaseEngineId } from '../../../../core/engines';
import { useQueryHistoryStore } from '../../../query-history';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { syncQueryExecution } from '../../../terminal/services/aiWorkspaceSyncService';
import { useAnalytics } from '../../../../core/analytics';
import { classifyDbError } from '../../../../core/analytics/dbErrorClassifier';
import { queryErrorTip } from '../utils/queryErrorTip';
import { getQueryErrorAction } from '../utils/queryErrorAction';
import { addBreadcrumb } from '../../../crash-dialog/services/breadcrumbService';
import { useAppStore } from '../../../../store/appStore';
import { invalidateAfterDdl } from '../../common/utils/ddlInvalidator';
import { usePendingRetryStore } from '../../connection-banner/store/pendingRetryStore';
import { isConnectionLostError as isBannerConnectionLostError } from '../../connection-banner/utils/isConnectionLostError';
import { maybeShowMilestoneToast } from '../../../review-prompt';
import { maybeTrackSampleDbFirstQuery } from '../../../sample-database';
import { markHasRunQuery } from '../../first-connect-bridge';
import { TRANSACTION_TAB_IDLE_RESET } from './useTransactionSessionsReleased';
import { safeConnectionName } from '../../../../types/safeConnectionDisplay';

/**
 * Which UI surface triggered a query execution. Reported in the
 * `feature:query_execute` analytics event so we can distinguish clicks,
 * shortcuts, context menus, etc.
 */
export type QueryExecuteSource =
  | 'execute-button'
  | 'keyboard-shortcut'
  | 'keyboard-shortcut-selection'
  | 'keyboard-shortcut-cursor'
  | 'inline-selection-execute'
  | 'inline-cursor-execute'
  | 'execute-with-plan'
  | 'select-top-context-menu'
  | 'peek-results'
  | 'edit-save'
  | 'results-grid-re-execute'
  | 'mcp'
  /**
   * The agent execute-with-confirm protocol (an agent in the embedded terminal
   * asking, via `execution.request.json`, for a tab to run). Deliberately NOT
   * `'mcp'`: that value means `ui_execute_query`, which runs with no per-query
   * confirmation, so folding the two together would make the consent-gated
   * surface unmeasurable in `feature:query_execute` / `query_error`.
   */
  | 'ai-agent-request'
  | 'auto-execute-on-mount'
  | 'vqe-execute-button'
  | 'vqe-keyboard-shortcut'
  /** The Visual view's own "Run on change" auto-run (Settings → UI → Visual editor). */
  | 'vqe-run-on-change';

interface UseQueryExecutionOptions {
  tabId: string;
  connectionId?: string;
  database?: string;
  /** When true, Run should also capture actual execution plan */
  includeActualPlan?: boolean;
  /** Database engine ID for engine-specific error detection */
  engineId?: DatabaseEngineId;
  /** Called on successful execution. hasResults is true if query returned rows */
  onSuccess?: (hasResults: boolean) => void;
  onError?: () => void;
  /** Called when a connection error is detected (e.g., connection lost) */
  onConnectionLost?: () => void;
  /**
   * Called after each execution completes with the structured error location
   * (when the engine provides one) or `undefined` (on success or when the engine
   * doesn't carry location info). Callers use this to place / clear Monaco
   * runtime-error squiggles independently from IntelliSense diagnostics.
   *
   * @param loc      The structured error position, or undefined to clear markers.
   * @param message  The error message text (for the Monaco marker tooltip).
   */
  onErrorLocation?: (loc: QueryExecutionError['errorLocation'] | undefined, message?: string) => void;
}

/** How long a query must run before the long-running indicator appears. */
export const LONG_RUNNING_THRESHOLD_MS = 8000;

interface QueryExecutionState {
  isExecuting: boolean;
  isCancelling: boolean;
  /** True after the query has been executing for ≥ LONG_RUNNING_THRESHOLD_MS. */
  isLongRunning: boolean;
  /**
   * True once the tab has run at least one query. Lets the status bar hide
   * its elapsed-time readout until there is a real measurement to show,
   * instead of a `00:00:00` that never ran.
   */
  hasExecuted: boolean;
  executionTime: string;
  rowCount: number;
  totalRowCount?: number;
  isTruncated?: boolean;
  /** Rows the last run's DML changed — set only when it returned no result set to count instead. */
  affectedRowCount?: number;
  lastMessage: string;
}

type ExecuteCurrentQueryOptions = {
  includeActualPlan?: boolean;
  /**
   * UI surface that triggered this execution. Defaults to 'execute-button'
   * if not specified (safest fallback for any call site we miss).
   */
  source?: QueryExecuteSource;
};

/** UI surface that triggered a query cancellation — kept low-cardinality for analytics. */
export type QueryCancelSource = 'toolbar' | 'long_running_indicator' | 'population';

/**
 * Outcome of one `executeCurrentQuery` call.
 *
 * The hook never rejects — every failure path is handled inline (toast,
 * message list, history entry). Callers that need to know whether the run
 * actually succeeded (the agent execute-with-confirm protocol reports it back
 * to the agent) read this instead. `errorMessage` is for the caller's own
 * decision-making; it can carry engine text, so anything that leaves the app
 * must map it to app-authored copy first.
 */
export type QueryExecutionOutcome = {
  success: boolean;
  errorMessage?: string;
};

interface UseQueryExecutionResult extends QueryExecutionState {
  setStatusMessage: (message: string) => void;
  executeCurrentQuery: (query: string, options?: ExecuteCurrentQueryOptions) => Promise<QueryExecutionOutcome>;
  cancelCurrentQuery: (opts?: { source?: QueryCancelSource }) => Promise<void>;
  /** Cancel the current execution via the existing cancel IPC. Convenience alias for cancelCurrentQuery. */
  cancelExecution: (opts?: { source?: QueryCancelSource }) => Promise<void>;
}

function createMessage(
  type: QueryMessage['type'],
  content: string,
  action?: QueryMessage['action']
): QueryMessage {
  return {
    id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    content,
    timestamp: new Date(),
    ...(action ? { action } : {})
  };
}

/**
 * Converts captured out-of-band server output (SQL Server PRINT / RAISERROR
 * severity <= 10, PostgreSQL RAISE NOTICE/INFO/WARNING — carried on
 * `QueryExecutionResult.messages`) into renderer `QueryMessage`s. Shared by
 * the success, cancelled, and failure branches below so all three place
 * captured messages the same way — the adapters deliberately keep whatever
 * a statement printed before it failed, so the failure and cancelled
 * branches must surface it too, not just success.
 */
function convertResultMessages(messages: QueryExecutionMessage[] | undefined): QueryMessage[] {
  if (!messages) return [];
  return messages.map((msg) =>
    createMessage(msg.type === 'error' ? 'error' : msg.type === 'warning' ? 'warning' : 'info', msg.text)
  );
}

/**
 * Convert resultSets to legacy format for backward compatibility
 */
function resultSetsToLegacyFormat(resultSets: QueryResultSet[] | undefined): {
  results: Record<string, unknown>[];
  columns: { id: string; name: string }[];
} {
  if (!resultSets || resultSets.length === 0) {
    return { results: [], columns: [] };
  }

  // For now, use the first result set (multi-resultset UI will be added later)
  const firstResultSet = resultSets[0];

  // Defensive checks: ensure columns and rows are always arrays (not just null/undefined, but any non-array)
  const safeColumns = Array.isArray(firstResultSet.columns) ? firstResultSet.columns : [];
  const safeRows = Array.isArray(firstResultSet.rows) ? firstResultSet.rows : [];

  const columns = safeColumns.map((col) => ({
    id: col.name,
    name: col.name,
  }));

  return {
    results: safeRows,
    columns,
  };
}

export function useQueryExecution({
  tabId,
  connectionId,
  database,
  includeActualPlan = false,
  engineId = 'mssql',
  onSuccess,
  onError,
  onConnectionLost,
  onErrorLocation,
}: UseQueryExecutionOptions): UseQueryExecutionResult {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const addHistoryEntry = useQueryHistoryStore((state) => state.addEntry);
  const { track } = useAnalytics();
  const timeoutSeconds = useAppStore((state) => state.queryExecutionSettings.timeoutSeconds);

  // Snapshot the human-readable connection name at the point of execution so
  // that history entries can still display it after the connection is deleted.
  // Reading the store directly (not via a selector hook) avoids re-rendering
  // the editor on unrelated connection-list updates. Matches the fallback used
  // by QueryHistorySearchPage (`alias || server`).
  const lookupConnectionName = useCallback((): string | undefined => {
    if (!connectionId) return undefined;
    const connection = useConnectionStore
      .getState()
      .connections.find((c) => c.id === connectionId);
    if (!connection) return undefined;
    return safeConnectionName(connection);
  }, [connectionId]);

  const [isExecuting, setIsExecuting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLongRunning, setIsLongRunning] = useState(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  const [executionTime, setExecutionTime] = useState('00:00:00');
  const [rowCount, setRowCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState<number | undefined>(undefined);
  const [isTruncated, setIsTruncated] = useState<boolean | undefined>(undefined);
  const [affectedRowCount, setAffectedRowCount] = useState<number | undefined>(undefined);
  const [lastMessage, setLastMessage] = useState('Connected');

  // Reset row-count state when the user switches the tab's connection — otherwise
  // the status bar lingers showing the previous connection's last result count.
  useEffect(() => {
    setRowCount(0);
    setTotalRowCount(undefined);
    setIsTruncated(undefined);
    setAffectedRowCount(undefined);
    setExecutionTime('00:00:00');
  }, [connectionId]);

  // Track the current executionId for cancellation
  const currentExecutionIdRef = useRef<string | undefined>(undefined);
  // Which execution the last Stop press targeted — the key the force escalation
  // is scoped to (see `nextCancelRequest`). Cleared when the execution settles,
  // so a Stop on the NEXT execution starts polite again.
  const lastCancelledExecutionIdRef = useRef<string | null>(null);
  // Track query start time for elapsed time calculation on cancel
  const queryStartTimeRef = useRef<number | null>(null);
  // Track run number for unique executionIds
  const runNumberRef = useRef(0);
  // Capture the latest (query, options) so the pending-retry action can replay them.
  const lastExecutionArgsRef = useRef<{ query: string; options?: ExecuteCurrentQueryOptions }>({ query: '' });
  // Stable ref to executeCurrentQuery so the retry action closure doesn't capture a stale callback.
  const executeCurrentQueryRef = useRef<(query: string, options?: ExecuteCurrentQueryOptions) => Promise<QueryExecutionOutcome>>(
    async () => ({ success: false })
  );

  // Live-tick the status-bar clock while a query runs. Without this the value
  // stays frozen on the previous result until the query completes.
  useEffect(() => {
    if (!isExecuting) return;
    const tick = () => {
      const start = queryStartTimeRef.current;
      if (start !== null) {
        setExecutionTime(formatExecutionTime(Date.now() - start));
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isExecuting]);

  // Long-running indicator: flip isLongRunning after LONG_RUNNING_THRESHOLD_MS.
  // Reset when execution ends.
  useEffect(() => {
    if (!isExecuting) {
      setIsLongRunning(false);
      return;
    }
    const timer = setTimeout(() => {
      setIsLongRunning(true);
      // engine/source/threshold let the release watch compute a long-running
      // SHARE per engine — a cross-engine slowdown signal that doesn't depend
      // on the frozen error taxonomy (a slow engine errors nothing).
      track('feature:query_long_running_shown', {
        engine: engineId,
        source: lastExecutionArgsRef.current.options?.source ?? 'execute-button',
        thresholdMs: LONG_RUNNING_THRESHOLD_MS,
      });
    }, LONG_RUNNING_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [isExecuting, track, engineId]);

  const executeCurrentQuery = useCallback(async (query: string, options?: ExecuteCurrentQueryOptions): Promise<QueryExecutionOutcome> => {
    if (!connectionId || !query.trim()) {
      return { success: false, errorMessage: 'No connection, or nothing to execute.' };
    }

    // Every branch below sets this before returning. The pessimistic default
    // means a path we forget can only ever under-report success, never
    // over-report it — a caller must not be told a run completed when it
    // didn't (the agent protocol writes `completed` off the back of it).
    let outcome: QueryExecutionOutcome = { success: false, errorMessage: 'Query execution did not complete.' };

    // Capture args so the pending-retry action can replay this exact invocation.
    lastExecutionArgsRef.current = { query, options };

    // Track query execution (type only, not content for privacy)
    addBreadcrumb('query_execute', 'execute');

    const shouldIncludeActualPlan = typeof options?.includeActualPlan === 'boolean' ? options.includeActualPlan : includeActualPlan;

    setIsExecuting(true);
    setIsCancelling(false);
    setHasExecuted(true);
    const startTime = Date.now();
    queryStartTimeRef.current = startTime;

    // Snapshot the human-readable connection name at THIS point (execution
    // start), not at completion. If a long-running query finishes after the
    // user renames or deletes the connection, the history entry should still
    // show the name that was in effect when the user hit Run. Reading at
    // completion (the previous behavior) captured the post-change name or
    // nothing at all. Engine id is captured from the hook option, which is
    // already stable for the lifetime of this call.
    const snapshotConnectionName = lookupConnectionName();

    // Generate unique execution ID
    runNumberRef.current += 1;
    const executionId = createExecutionId(tabId, runNumberRef.current);
    currentExecutionIdRef.current = executionId;
    // A new execution never inherits the previous one's escalation.
    lastCancelledExecutionIdRef.current = null;

    // Add "Executing query..." message
    const executingMessage = createMessage('info', 'Executing query...');
    const currentTab = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
    const existingMessages = (currentTab?.type === 'query' ? currentTab.content?.messages : []) || [];
    // The renderer's claim the backend checks: a tab in an active transaction
    // must run on its session or be refused — never silently auto-committed.
    // Transactions live on query tabs only.
    const txState = currentTab?.type === 'query' ? currentTab.content?.transactionState : undefined;
    const transactionSessionExpected = txState === 'active' || txState === 'error';
    const transactionSessionGeneration =
      transactionSessionExpected && currentTab?.type === 'query'
        ? currentTab.content?.transactionSessionGeneration
        : undefined;
    const resultDisplayMode =
      currentTab?.type === 'query' ? currentTab.content?.editorOptions?.resultDisplayMode : undefined;
    const baseMessages = resultDisplayMode === 'plaintext' ? [] : existingMessages;

    // ── Time-to-first-rows: dispatch the IPC BEFORE the store writes ───────
    // `updateTabContent` is not free: the zustand `set` rebuilds the tab array,
    // spreads `messages`, and synchronously runs every non-React subscriber
    // (useMcpContextSync rebuilds four workspace snapshots, sessionAutoSave
    // arms its debounce) before React even schedules the commit. Doing that
    // first delayed the request leaving the renderer by ~15-20 ms on every run.
    //
    // The reorder is unobservable to anything outside this function: both
    // statements run in the SAME synchronous block, so no IPC reply, progress
    // event, timer, or React commit can interleave between them.
    //   • `currentTab` / `existingMessages` / `baseMessages` are captured ABOVE
    //     (pre-clear state) and are only read afterwards — unchanged.
    //   • `usePopulationProgress` subscribes from an effect gated on
    //     `isExecuting`, which flips via `setIsExecuting(true)` further up. That
    //     commit (and its effect) already ran AFTER this whole block, both
    //     before and after this change — the subscription has never been
    //     established before the request was dispatched. Progress events are
    //     delivered as fresh event-loop tasks by the main process, so the
    //     earliest one still lands after the commit; the ~15-20 ms shift only
    //     moves the dispatch, not the ordering of anything the renderer sees.
    //   • `cacheGeneration` / `resultSetCacheUris` are cleared below; the only
    //     readers (usePopulationProgress's generation guard, useWindowedData)
    //     read them from a later render, never mid-block. Nothing reacts to the
    //     clear by issuing a `result-cache:discard` that could race the new
    //     sources (discards are user-driven: connection change, unpin).
    // `executeQueryV2` is an `async function`, so this can never throw
    // synchronously; the try/catch boundary below is therefore unchanged.
    //
    // timeoutSeconds: positive = use that timeout, 0 = no timeout (both adapters
    // treat timeoutMs === 0 as unlimited). We ALWAYS plumb the value — never
    // leave it undefined — because adapter-side fallbacks predate the 300 s
    // default bump and would silently re-impose a 15 s timeout otherwise.
    const timeoutMs = timeoutSeconds * 1000;
    const executionPromise = executeQueryV2({
      connectionId,
      sql: query,
      database,
      executionId,
      tabId,
      options: {
        ...(shouldIncludeActualPlan ? { plan: { mode: 'actual' } } : {}),
        timeoutMs,
        // Query Editor renders windowed results (cache URIs + progress
        // events), so it opts into STS windowed mode on SSTS-routed MSSQL
        // connections. Callers without this flag get rows inline.
        streamResults: true,
        ...(transactionSessionExpected ? { transactionSessionExpected: true } : {}),
        ...(transactionSessionGeneration !== undefined ? { transactionSessionGeneration } : {}),
      }
    });
    // The promise is awaited a few statements down. Attach a no-op handler so a
    // throw from `updateTabContent` (which would skip the await) can't surface
    // as an unhandled rejection — this derived promise is discarded and the
    // original still rejects into the `try` below.
    void executionPromise.catch(() => {});

    updateTabContent(tabId, {
      messages: [...baseMessages, executingMessage],
      // Everything before this index belongs to earlier runs — the Output
      // surfaces slice from here so they describe THIS execution only.
      messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
      resultSets: [],
      results: [],
      columns: [],
      executionPlan: undefined,
      resultSetCacheUris: undefined,
      cacheGeneration: undefined,
    });

    try {
      const result = await executionPromise;

      if (!result) {
        throw new Error('No response from query engine.');
      }

      const elapsed = Date.now() - startTime;
      const formattedTime = formatExecutionTime(elapsed);
      setExecutionTime(formattedTime);

      if (result.success && result.resultSets) {
        outcome = { success: true };
        const stats = result.stats;
        const returnedRowCount = stats?.returnedRowCount ?? 0;
        const truncated = stats?.truncated === true;
        const rowSummary = summarizeExecutionRows(stats, result.resultSets);

        // When cacheUri is present, rows were stripped by Electron.
        // ResultsGrid will fetch windowed data on demand via useWindowedData.
        // We keep resultSets with column metadata but empty rows.
        const displayResultSets = result.resultSets;

        // Convert to legacy format for backward compatibility
        const { results, columns } = resultSetsToLegacyFormat(displayResultSets);

        const successMessage = createMessage(
          'success',
          `Query executed successfully. ${formatExecutionRowSummary(rowSummary)} in ${formattedTime}.`
        );

        // Add any messages from the execution
        const allMessages = [...baseMessages, successMessage, ...convertResultMessages(result.messages)];

        updateTabContent(tabId, {
          resultSets: displayResultSets,
          results,
          columns,
          messages: allMessages,
          // Everything before this index belongs to earlier runs — the Output
          // surfaces slice from here so they describe THIS execution only.
          messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
          stats,
          // Slice 6 — clear any previously-set connection-failure banner once
          // an execution succeeds against this scope database.
          connectionFailure: undefined,
          ...(result.resultSetCacheUris?.length ? { resultSetCacheUris: result.resultSetCacheUris, cacheGeneration: runNumberRef.current } : {}),
          ...(result.plan ? { executionPlan: result.plan } : {}),
        });

        setRowCount(rowSummary.returnedRowCount);
        setTotalRowCount(rowSummary.totalRowCount);
        setIsTruncated(truncated);
        // The status bar names the affected count only when nothing came back
        // to count; a run that also returned rows keeps its row counter.
        setAffectedRowCount(rowSummary.hasReturnedResultSet ? undefined : rowSummary.affectedRowCount);
        // The row count itself now lives solely in the status bar's dedicated
        // row-count widget (which also carries the truncated/"preview" state
        // via its own icon + tooltip) — restating it here would duplicate the
        // same number in the same status bar.
        setLastMessage('Query executed successfully');

        // Save to query history (snapshot connectionName/engineId so the entry
        // remains legible after the connection is deleted).
        addHistoryEntry({
          sql: query,
          connectionId,
          connectionName: snapshotConnectionName,
          engineId,
          database,
          executedAt: new Date(),
          success: true,
          rowCount: returnedRowCount,
          executionTimeMs: elapsed,
        });

        // Clear any runtime-error squiggles from the previous failed execution.
        onErrorLocation?.(undefined, undefined);

        // A SELECT with columns but zero rows (e.g. WHERE 1=0) still has a "result"
        // worth showing — surface it on the results tab so the user can see headers.
        const hasResultsToShow =
          returnedRowCount > 0
          || (Array.isArray(result.resultSets)
            && result.resultSets.some((rs) => Array.isArray(rs?.columns) && rs.columns.length > 0));
        onSuccess?.(hasResultsToShow);

        // Invalidate metadata caches when the executed SQL is DDL.
        // Logic is shared with the Table Designer apply path via the helper
        // (INT-15) so both paths invalidate identically and can't drift.
        if (connectionId) invalidateAfterDdl(connectionId, query);

        // Track successful query execution. `currentTab` was already read
        // from the store at execution start (used for baseMessages); reuse
        // it so we don't look up the same tab twice.
        const transactionMode =
          currentTab?.type === 'query'
            ? currentTab.content?.transactionMode ?? 'auto'
            : 'auto';
        track('feature:query_execute', {
          engine: engineId,
          source: options?.source ?? 'execute-button',
          transactionMode,
          // Raw ms (latencyMs precedent on completion.resolved): the 1.4.17
          // per-engine duration distribution is the baseline that makes a
          // 1.4.18 execution-path latency regression judgeable at all.
          durationMs: elapsed,
          ...(typeof stats?.timeToFirstBatchMs === 'number'
            ? { firstBatchMs: Math.round(stats.timeToFirstBatchMs) }
            : {}),
        });

        // Count completed-without-error executions toward the review milestone;
        // fires the one-time celebratory toast on the 100th success.
        maybeShowMilestoneToast();

        // First successful query against the bundled sample DB (one-shot).
        maybeTrackSampleDbFirstQuery(connectionId);

        // Mark "any query has run" so the first-connect bridge never fires for
        // this install again (parity with the Table Explorer load seam).
        markHasRunQuery();

        // Sync execution info to AI workspace (including history)
        const tabTitle = currentTab?.title || 'Query';
        syncQueryExecution(tabId, tabTitle, {
          success: true,
          duration: elapsed,
          rowCount: returnedRowCount,
          query,
          columns: columns.map(c => ({ name: c.name, type: 'unknown' })),
          rows: results.slice(0, 100), // Limit rows in history to first 100
        });
      } else if (result.cancelled) {
        // A cancelled run is not a completed run — never report it as success.
        outcome = { success: false, errorMessage: 'Query cancelled by user.' };
        // Converted BEFORE the cancellation line is created: `createMessage`
        // stamps `new Date()`, and the Output / plaintext views sort by that
        // stamp — so a cancellation message minted first would sort AHEAD of
        // the progress lines it must follow whenever the two creations land on
        // either side of a millisecond boundary. Array order alone is not
        // enough to hold the intended order here.
        const capturedMessages = convertResultMessages(result.messages);
        const cancelledMessage = createMessage('warning', `Query cancelled by user after ${formattedTime}.`);
        const cancelledRowCount = (result.resultSets || []).reduce((sum, rs) => sum + (rs.rowCount ?? rs.rows?.length ?? 0), 0);
        updateTabContent(tabId, {
          resultSets: result.resultSets || [],
          results: [],
          columns: [],
          // Whatever the statement printed before the user's cancel landed
          // (PRINT / RAISE NOTICE progress lines) is still useful context —
          // surface it ahead of the cancellation line, same as the failure
          // branches below.
          messages: [...baseMessages, ...capturedMessages, cancelledMessage],
          // Everything before this index belongs to earlier runs — the Output
          // surfaces slice from here so they describe THIS execution only.
          messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
          ...(result.resultSetCacheUris?.length ? { resultSetCacheUris: result.resultSetCacheUris, cacheGeneration: runNumberRef.current } : {}),
        });
        setRowCount(cancelledRowCount);
        setTotalRowCount(cancelledRowCount);
        setAffectedRowCount(undefined);
        setLastMessage('Query cancelled');
      } else {
        const errorText = result.error?.message || 'Query execution failed.';
        outcome = { success: false, errorMessage: errorText };

        // The backend refused a statement for a transaction it no longer holds
        // (`TRANSACTION_SESSION_MISSING`): the tab's state was stale. Reset it
        // so the next run begins a fresh transaction instead of failing again.
        if (result.error?.code === 'TRANSACTION_SESSION_MISSING') {
          updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
        }
        // The server's transaction may have been rolled back (MySQL: a lock
        // wait timeout whose status check failed): the tab's transaction is in
        // error — Commit off, Rollback only — and says why after the
        // statement's own error (Codex r10).
        const stateUnknownMessage = result.error?.code === 'TRANSACTION_STATE_UNKNOWN'
          ? result.error.transactionStateMessage
          : undefined;
        if (result.error?.code === 'TRANSACTION_STATE_UNKNOWN') {
          updateTabContent(tabId, { transactionState: 'error' });
        }

        // Classify once for BOTH the telemetry event below and the actionable
        // tip appended under the error message. `code` is the driver error
        // code only (classifier-sanitized) — never message text.
        //
        // `sqlErrorCode` (the server's error number) outranks `code`: tedious
        // wraps every request failure in the same generic EREQUEST, which
        // classifies nothing and, when preferred, discards the number that
        // does (field data: 34/37 unknown-category query errors carried
        // code=EREQUEST next to an unused sqlErrorCode).
        const errorClassification = classifyDbError(
          engineId,
          errorText,
          result.error?.sqlErrorCode ?? result.error?.code
        );

        // Set in the non-connection-lost branch below when the tip row carries
        // a one-click action; reported on `feature:query_error` so the action's
        // click-through rate is measurable (fix_clicked / query_error-with-fixAction).
        let shownFixActionKind: string | undefined;

        // Check if this is a connection lost error.
        // Uses both the engine-specific patterns (covers DB-level connection drops)
        // and the banner's canonical strings ("Connection not found: <uuid>",
        // "No connection available") which are thrown by the driver manager layer.
        // ONLY if it's not a database-specific error (isDatabaseError = true) —
        // database errors (offline DB, no permission) don't mean the connection is lost.
        // Nor does `TRANSACTION_SESSION_INTERRUPTED`: only the tab's pinned
        // transaction session was closed (its message says so; the MSSQL and
        // MySQL patterns would read "connection … closed" as a lost connection).
        const isConnLost =
          !result.error?.isDatabaseError &&
          result.error?.code !== 'TRANSACTION_SESSION_INTERRUPTED' &&
          (isConnectionLostError(engineId, errorText) || isBannerConnectionLostError(errorText));

        if (isConnLost) {
          // Queue a pending retry so the banner can offer "Connect and run query".
          if (connectionId) {
            usePendingRetryStore.getState().setPendingRetry({
              tabId,
              requires: [connectionId],
              label: 'run query',
              // The banner action is void-typed; the replay's outcome is only
              // meaningful to the caller that asked for it, not to the retry.
              action: async () => {
                await executeCurrentQueryRef.current(
                  lastExecutionArgsRef.current.query,
                  lastExecutionArgsRef.current.options,
                );
              },
            });
          }
          // Show a friendly one-liner instead of the raw UUID error. Captured
          // messages (e.g. PRINT lines emitted before the connection dropped)
          // still happened before the failure, so they go ahead of it.
          updateTabContent(tabId, {
            resultSets: [],
            results: [],
            columns: [],
            messages: [
              ...baseMessages,
              ...convertResultMessages(result.messages),
              createMessage(
                'error',
                "Connection isn't active — use the banner above to reconnect.",
              ),
            ],
          // Everything before this index belongs to earlier runs — the Output
          // surfaces slice from here so they describe THIS execution only.
          messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
            connectionFailure: undefined,
          });
        } else {
          // Slice 6 — surface a per-tab connection-failure banner when the engine
          // flagged this as a database-scoped failure (login can't open DB, no
          // permission, DB offline, etc.). This is what makes the "Cannot open
          // database 'X'" case actionable from inside the tab.
          const isDbFailure = result.error?.isDatabaseError === true;
          const connectionFailureUpdate = isDbFailure
            ? {
                connectionFailure: {
                  message: errorText,
                  ...(result.error?.sqlErrorCode !== undefined ? { sqlErrorCode: result.error.sqlErrorCode } : {}),
                  ...(database ? { scopeDatabase: database } : {}),
                },
              }
            : { connectionFailure: undefined };

          // Categories with a clear next step get a short tip line under the
          // error (wrong-database object lookups, missing permissions, locks,
          // type-conversion issues). Others render exactly as before. Where
          // the next step exists in-app, the tip row also carries a one-click
          // action (queryErrorAction.ts) rendered as a button by MessagesPanel.
          const tip = queryErrorTip(errorClassification.category);
          const fixAction = tip
            ? getQueryErrorAction(errorClassification.category, engineId)
            : undefined;
          shownFixActionKind = fixAction?.kind;
          // Captured messages (PRINT / RAISERROR severity <= 10, RAISE
          // NOTICE/INFO/WARNING) happened before the statement that failed —
          // place them ahead of the error line, same as the success branch.
          updateTabContent(tabId, {
            resultSets: [],
            results: [],
            columns: [],
            messages: [
              ...baseMessages,
              ...convertResultMessages(result.messages),
              createMessage('error', errorText),
              ...(stateUnknownMessage ? [createMessage('error', stateUnknownMessage)] : []),
              ...(tip ? [createMessage('info', tip, fixAction)] : []),
            ],
          // Everything before this index belongs to earlier runs — the Output
          // surfaces slice from here so they describe THIS execution only.
          messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
            ...connectionFailureUpdate,
          });
        }

        setLastMessage('Query failed');
        setRowCount(0);
        setTotalRowCount(undefined);
        setIsTruncated(undefined);
        setAffectedRowCount(undefined);

        // Save failed query to history (snapshot connectionName/engineId so
        // the entry remains legible after the connection is deleted).
        addHistoryEntry({
          sql: query,
          connectionId,
          connectionName: snapshotConnectionName,
          engineId,
          database,
          executedAt: new Date(),
          success: false,
          executionTimeMs: elapsed,
          errorMessage: errorText,
        });

        // Surface structured error location to caller (e.g. for Monaco squiggle placement).
        // Pass undefined when errorLocation is absent so callers clear stale markers.
        onErrorLocation?.(result.error?.errorLocation, result.error?.message);

        if (isConnLost) {
          onConnectionLost?.();
        } else {
          onError?.();
        }

        // Sync execution info to AI workspace (including history)
        const tabTitle = currentTab?.title || 'Query';
        syncQueryExecution(tabId, tabTitle, {
          success: false,
          duration: elapsed,
          rowCount: 0,
          query,
          error: errorText,
        });

        // Track failed query execution (release-observability). Never carries
        // error text, SQL, or anything high-cardinality — just the engine and
        // a sanitized error category. This `else` branch only runs when
        // `result.success` is false AND `result.cancelled` is falsy (the
        // `else if (result.cancelled)` arm above already claimed cancellations),
        // so no separate cancel guard is needed here.
        track('feature:query_error', {
          engine: engineId,
          category: errorClassification.category,
          ...(errorClassification.code ? { code: errorClassification.code } : {}),
          ...(shownFixActionKind ? { fixAction: shownFixActionKind } : {}),
          source: options?.source ?? 'execute-button',
          durationMs: elapsed,
        });
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'An unexpected error occurred.';
      const elapsed = Date.now() - startTime;
      outcome = { success: false, errorMessage: errorText };

      setLastMessage('Query failed');
      setRowCount(0);
      setTotalRowCount(undefined);
      setIsTruncated(undefined);
      setAffectedRowCount(undefined);

      // Save failed query to history (exception case). Also snapshot
      // connectionName/engineId here — the original Low #5 follow-up wired
      // these fields into the success/result-error branches but missed this
      // catch, so thrown errors (network failures, timeouts, cancellation
      // rejections) silently dropped the fields and left deleted-connection
      // entries showing truncated UUID slices in the history UI.
      addHistoryEntry({
        sql: query,
        connectionId,
        connectionName: snapshotConnectionName,
        engineId,
        database,
        executedAt: new Date(),
        success: false,
        executionTimeMs: elapsed,
        errorMessage: errorText,
      });

      // Check if this is a connection lost error.
      // Uses both the engine-specific patterns and the banner's canonical strings.
      if (isConnectionLostError(engineId, errorText) || isBannerConnectionLostError(errorText)) {
        // Queue a pending retry so the banner can offer "Connect and run query".
        if (connectionId) {
          usePendingRetryStore.getState().setPendingRetry({
            tabId,
            requires: [connectionId],
            label: 'run query',
            // See the note on the result-error branch above.
            action: async () => {
              await executeCurrentQueryRef.current(
                lastExecutionArgsRef.current.query,
                lastExecutionArgsRef.current.options,
              );
            },
          });
        }
        // Show a friendly one-liner instead of the raw UUID error.
        updateTabContent(tabId, {
          resultSets: [],
          results: [],
          columns: [],
          messages: [
            ...baseMessages,
            createMessage(
              'error',
              "Connection isn't active — use the banner above to reconnect.",
            ),
          ],
        messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
        });
        onConnectionLost?.();
      } else {
        const errorMessage = createMessage('error', errorText);
        updateTabContent(tabId, {
          resultSets: [],
          results: [],
          columns: [],
          messages: [...baseMessages, errorMessage],
          // Everything before this index belongs to earlier runs — the Output
          // surfaces slice from here so they describe THIS execution only.
          messagesRunStart: baseMessages.length,
      messagesRunEnd: undefined,
        });
        onError?.();
      }

      // Sync execution info to AI workspace (including history)
      const tabTitle = currentTab?.title || 'Query';
      syncQueryExecution(tabId, tabTitle, {
        success: false,
        duration: elapsed,
        rowCount: 0,
        query,
        error: errorText,
      });

      // Track failed query execution (release-observability). Same shape as
      // the result-error branch above; no cancel guard needed here either —
      // a user-initiated cancel rejects through `cancelCurrentQuery`'s own
      // try/catch (which already fires `feature:query_cancel`), not this
      // `executeCurrentQuery` catch block.
      const thrownClassification = classifyDbError(engineId, errorText);
      track('feature:query_error', {
        engine: engineId,
        category: thrownClassification.category,
        ...(thrownClassification.code ? { code: thrownClassification.code } : {}),
        source: options?.source ?? 'execute-button',
        durationMs: elapsed,
      });
    } finally {
      setIsExecuting(false);
      setIsCancelling(false);
      queryStartTimeRef.current = null;
      currentExecutionIdRef.current = undefined;
      lastCancelledExecutionIdRef.current = null;
    }

    return outcome;
  }, [connectionId, database, includeActualPlan, engineId, tabId, updateTabContent, addHistoryEntry, lookupConnectionName, onSuccess, onError, onConnectionLost, onErrorLocation, track, timeoutSeconds]);

  // Keep the ref in sync so retry actions always call the latest callback.
  executeCurrentQueryRef.current = executeCurrentQuery;

  const cancelCurrentQuery = useCallback(async (opts?: { source?: QueryCancelSource }) => {
    const executionId = currentExecutionIdRef.current;
    if (!isExecuting || !executionId) {
      return;
    }

    // Second press against the SAME execution: the polite cancel is still
    // outstanding, so stop WAITING for the server to honour it and return the
    // query now — on Oracle that destroys the client socket, so the query
    // returns at once while the server session finishes its own wait. Keyed to
    // the execution id, never to
    // `isCancelling` — a replacement execution must start polite again.
    const { request, force } = nextCancelRequest(executionId, lastCancelledExecutionIdRef.current);
    lastCancelledExecutionIdRef.current = executionId;

    // Track query cancellation
    addBreadcrumb('query_execute', 'cancel');
    track('feature:query_cancel', {
      source: opts?.source ?? 'toolbar',
      engine: engineId,
      force,
      // How long the user waited before giving up — a rising cancel-at-Ns
      // pattern is an early slowness signal the error rows can't see.
      elapsedMs: queryStartTimeRef.current ? Date.now() - queryStartTimeRef.current : 0,
    });

    setIsCancelling(true);

    const cancelTab = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
    const existingMessages = (cancelTab?.type === 'query' ? cancelTab.content?.messages : []) || [];

    // Add "Cancelling query..." message — only for the first press; the forced
    // press would otherwise stack a second identical line under the first.
    if (!force) {
      const cancellingMessage = createMessage('info', 'Cancelling query...');
      updateTabContent(tabId, {
        messages: [...existingMessages, cancellingMessage]
      });
    }

    try {
      // Use new V2 API with executionId
      const result = await cancelQueryV2(request);

      if (!result) {
        throw new Error('No response from cancel request.');
      }

      // THE EXECUTION OWNS ITS TERMINAL STATE. If it is no longer the tab's
      // current one by the time this reply lands, this handler writes nothing:
      //
      //  - it settled — its own branch already wrote the outcome, the messages
      //    and the elapsed time. A cancel REQUEST succeeding does not prove the
      //    work was cancelled (the server may have finished first), so writing
      //    "Query cancelled" over a completed run would be a lie — and it would
      //    do it from `existingMessages`, a pre-cancel snapshot that predates
      //    the PRINT / RAISE NOTICE output the adapter returned, and with a
      //    zeroed elapsed time (`queryStartTimeRef` is cleared in that
      //    `finally`);
      //  - or another run replaced it, whose messages and `isCancelling` are
      //    not this reply's to touch either.
      //
      // Both leave `isCancelling` to whoever owns it now. What makes this safe
      // is that the adapters report a genuine interruption themselves — see
      // `pgsqlAdapter`'s post-statement-loop check — so nothing is lost by
      // staying quiet here.
      if (currentExecutionIdRef.current !== executionId) return;
      const messagesToExtend = existingMessages;

      if (result.success) {
        // Calculate elapsed time
        const elapsed = queryStartTimeRef.current ? Date.now() - queryStartTimeRef.current : 0;
        const formattedTime = formatExecutionTime(elapsed);
        setExecutionTime(formattedTime);

        const cancelledMessage = createMessage('warning', `Query cancelled by user after ${formattedTime}.`);
        updateTabContent(tabId, {
          messages: [...messagesToExtend, cancelledMessage]
        });
        setLastMessage('Query cancelled');
      } else {
        setIsCancelling(false);
        const cancelError = typeof result.error === 'string' ? result.error
          : (result.error as { message?: string } | undefined)?.message;
        const errorMessage = createMessage(
          'error',
          cancelError || 'Failed to cancel query.'
        );
        updateTabContent(tabId, {
          messages: [...messagesToExtend, errorMessage]
        });
      }
    } catch (error) {
      // Same rule as above: a reply about an execution the tab has moved on
      // from is not ours to write.
      if (currentExecutionIdRef.current !== executionId) return;
      setIsCancelling(false);
      const errorMessage = createMessage(
        'error',
        error instanceof Error ? error.message : 'Error cancelling query.'
      );
      updateTabContent(tabId, {
        messages: [...existingMessages, errorMessage]
      });
    }
    // Note: isCancelling is reset in executeCurrentQuery's finally block when the query actually stops
  }, [isExecuting, tabId, updateTabContent, track, engineId]);

  return {
    isExecuting,
    isCancelling,
    isLongRunning,
    hasExecuted,
    executionTime,
    rowCount,
    totalRowCount,
    isTruncated,
    affectedRowCount,
    lastMessage,
    setStatusMessage: setLastMessage,
    executeCurrentQuery,
    cancelCurrentQuery,
    cancelExecution: cancelCurrentQuery,
  };
}
